import { tintOnWhite } from '@/theme/colorUtils';
import { r, s } from '@/theme/tokens';
import { chartStatusDarkenCoefficient, uiTokens } from '@/theme/uiTokens';
import { Box, Text, Tooltip, useTheme } from '@/ui/primitives';
import { chartSwatchRadiusPx, chartSwatchSizePx } from '@/ui/primitives/ChartSwatch';
import { darken } from '@/ui/primitives/colorUtils';
import {
  buildTooltipOverlaySurfaceSx,
  tooltipOverlayBodyFontWeight,
  tooltipOverlayFontSize,
  tooltipOverlayLabelFontWeight,
  tooltipOverlayLineHeight,
  tooltipOverlayMetaFontSize,
  tooltipOverlayMetricFontWeight,
  tooltipOverlayPaddingX,
  tooltipOverlayPaddingY,
  tooltipOverlayTextColor,
} from '@/ui/primitives/tooltipOverlayStyles';
import { TABLE_TOOLTIP_ENTER_DELAY_MS } from '../tableTooltip';
import type { ReactElement } from 'react';

export type CountSegment = { label: string; value: number; color: string };
const statusSegmentColors = new Set(Object.values(uiTokens.statusColors));

type StackedCountBarProps = {
  segments: CountSegment[];
  total: number;
  totalLabel?: string;
  emptyLabel?: string;
  colorAlpha?: number;
};

const buildSegmentLines = (
  segments: CountSegment[],
  total: number,
): Array<{ label: string; count: number; percent: number; color: string }> =>
  segments
    .map((segment) => ({
      label: segment.label,
      count: segment.value,
      percent: total > 0 ? Math.round((segment.value / total) * 100) : 0,
      color: segment.color,
    }))
    .filter((line) => line.count > 0);

const resolveSegmentColor = (color: string, isDarkMode: boolean, alpha: number): string =>
  isDarkMode
    ? darken(color, statusSegmentColors.has(color) ? chartStatusDarkenCoefficient : 1 - alpha)
    : tintOnWhite(color, alpha);

export const StackedCountBar = ({
  segments,
  total,
  totalLabel,
  emptyLabel = '-',
  colorAlpha,
}: StackedCountBarProps): ReactElement => {
  const theme = useTheme();
  const statusBarHeight = s('md');
  const statusBarRadius = r('sm');
  const BAR_STYLE_CONFIG = {
    light: { colorAlpha: 0.8 },
    dark: { colorAlpha: 0.8 },
  } as const;
  const resolvedAlpha =
    colorAlpha ??
    (theme.palette.mode === 'dark'
      ? BAR_STYLE_CONFIG.dark.colorAlpha
      : BAR_STYLE_CONFIG.light.colorAlpha);
  const isDarkMode = theme.palette.mode === 'dark';

  if (total <= 0) {
    const emptyCellPaddingY = s('sm');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: emptyCellPaddingY, width: '100%' }}>
        <Text role="body" component="span" color="textSecondary">
          {emptyLabel}
        </Text>
      </Box>
    );
  }

  const lines = buildSegmentLines(segments, total);
  return (
    <Tooltip
      enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
      title={
        <Box sx={{ color: tooltipOverlayTextColor, width: 'fit-content' }}>
          {totalLabel ? (
            <Box
              sx={{
                px: tooltipOverlayPaddingX,
                pt: '0px',
                pb: '2px',
                borderBottom: `1px solid ${theme.app.colors.border}`,
              }}
            >
              <Text
                role="captionSmall"
                sx={{
                  color: 'inherit',
                  fontSize: tooltipOverlayFontSize,
                  fontWeight: tooltipOverlayLabelFontWeight,
                  lineHeight: tooltipOverlayLineHeight,
                }}
              >
                {totalLabel}
              </Text>
            </Box>
          ) : null}
          <Box sx={{ pt: '0px', pb: '0px' }}>
            {lines.map((line, index) => (
              <Box
                key={line.label}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '16px 1fr auto auto',
                  alignItems: 'center',
                  columnGap: s('sm'),
                  pl: '1px',
                  pr: '4px',
                  py: '2px',
                  ...(index === 0 ? { pt: totalLabel ? '4px' : '1px' } : {}),
                  ...(index === lines.length - 1 ? { pb: '2px' } : {}),
                }}
              >
                <Box
                  sx={{
                    width: `${String(chartSwatchSizePx)}px`,
                    height: `${String(chartSwatchSizePx)}px`,
                    borderRadius: `${String(chartSwatchRadiusPx)}px`,
                    backgroundColor: resolveSegmentColor(line.color, isDarkMode, resolvedAlpha),
                    justifySelf: 'center',
                  }}
                />
                <Text
                  role="captionSmall"
                  sx={{
                    color: 'inherit',
                    fontSize: tooltipOverlayFontSize,
                    fontWeight: tooltipOverlayBodyFontWeight,
                    lineHeight: tooltipOverlayLineHeight,
                  }}
                >
                  {line.label}
                </Text>
                <Text
                  role="captionSmall"
                  sx={{
                    color: 'inherit',
                    textAlign: 'right',
                    fontSize: tooltipOverlayFontSize,
                    fontWeight: tooltipOverlayMetricFontWeight,
                    lineHeight: tooltipOverlayLineHeight,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {line.count}
                </Text>
                <Text
                  role="captionSmall"
                  sx={{
                    color: tooltipOverlayTextColor,
                    textAlign: 'right',
                    minWidth: '44px',
                    fontSize: tooltipOverlayMetaFontSize,
                    fontWeight: tooltipOverlayBodyFontWeight,
                    lineHeight: tooltipOverlayLineHeight,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ({line.percent}%)
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      }
      placement="bottom"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            ...buildTooltipOverlaySurfaceSx(theme),
            fontSize: tooltipOverlayFontSize,
            lineHeight: tooltipOverlayLineHeight,
            fontWeight: theme.typography.fontWeightRegular,
            paddingTop: tooltipOverlayPaddingY,
            paddingBottom: tooltipOverlayPaddingY,
            paddingLeft: tooltipOverlayPaddingX,
            paddingRight: tooltipOverlayPaddingX,
          },
        },
      }}
    >
      <Box
        role="img"
        aria-label={totalLabel ?? 'Status breakdown'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: statusBarHeight,
          width: '100%',
          borderRadius: statusBarRadius,
          overflow: 'hidden',
          backgroundColor: theme.palette.divider,
        }}
      >
        {segments.map((segment) => {
          if (segment.value <= 0) return null;
          return (
            <Box
              key={segment.label}
              sx={{
                width: `${(segment.value / total) * 100}%`,
                height: '100%',
                backgroundColor: resolveSegmentColor(segment.color, isDarkMode, resolvedAlpha),
              }}
            />
          );
        })}
      </Box>
    </Tooltip>
  );
};
